// Shopping cart hook with localStorage persistence
import { useState, useEffect, useCallback, useRef } from 'react';
import { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './useAuth';
import { trackAddedToCartEvent } from './useKlaviyoTracking';

export interface CartItem {
  id: string;
  productId: string;
  name: string;
  brand: string;
  businessId: string;
  price: number;
  size: string;
  quantity: number;
  imageUrl: string;
}

const CART_STORAGE_KEY = 'fash_cart';

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Mirror of `items` so stable callbacks can read the latest cart
  const itemsRef = useRef<CartItem[]>([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setItems(parsed);
        itemsRef.current = parsed;
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    }
    setLoading(false);
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (!loading) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    }
  }, [items, loading]);

  const notifyKlaviyoCart = useCallback((cartItems: CartItem[]) => {
    if (!user?.email || cartItems.length === 0) return;
    trackAddedToCartEvent({
      itemCount: cartItems.reduce((sum, i) => sum + i.quantity, 0),
      subtotalCents: Math.round(cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0) * 100),
      businessId: cartItems[0]?.businessId,
      businessName: cartItems[0]?.brand,
      productNames: cartItems.map((i) => i.name),
    });
  }, [user?.email]);

  /**
   * Add an item to the bag. Orders deliver from ONE boutique at a time, so
   * adding from a different boutique asks the shopper to start a new bag.
   * Returns false if the shopper declined (nothing was added).
   */
  const addItem = useCallback((item: Omit<CartItem, 'id'>): boolean => {
    const current = itemsRef.current;
    if (current.length > 0) {
      const inBag = current[0].businessId || current[0].brand;
      const incoming = item.businessId || item.brand;
      if (inBag && incoming && inBag !== incoming) {
        const startNewBag = window.confirm(
          `Your bag has items from ${current[0].brand}. Orders are delivered from one boutique at a time.\n\nStart a new bag with ${item.brand}? Your current bag will be emptied.`
        );
        if (!startNewBag) return false;
        const newItems = [{ ...item, id: `${item.productId}_${item.size}_${Date.now()}` }];
        setItems(newItems);
        notifyKlaviyoCart(newItems);
        return true;
      }
    }

    setItems(prev => {
      // Check if item already exists with same product and size
      const existingIndex = prev.findIndex(
        i => i.productId === item.productId && i.size === item.size
      );

      let next: CartItem[];
      if (existingIndex >= 0) {
        next = [...prev];
        next[existingIndex] = {
          ...next[existingIndex],
          quantity: next[existingIndex].quantity + item.quantity,
        };
      } else {
        next = [...prev, { ...item, id: `${item.productId}_${item.size}_${Date.now()}` }];
      }
      notifyKlaviyoCart(next);
      return next;
    });
    return true;
  }, [notifyKlaviyoCart]);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(itemId);
      return;
    }
    
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, quantity } : item
    ));
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return {
    items,
    loading,
    itemCount,
    subtotal,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
  };
}

// Create a context for cart if needed across components
interface CartContextValue extends ReturnType<typeof useCart> {}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const cart = useCart();
  
  return (
    <CartContext.Provider value={cart}>
      {children}
    </CartContext.Provider>
  );
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within CartProvider');
  }
  return context;
}

